import { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import axios from 'axios';
import { isValidUrl } from '../util/SearchUtil';

const HeaderSearchbar = () => {
  const [searchText, setSearchText] = useState('');
  const [movieResults, setMovieResults] = useState([]);
  const [tvResults, setTvResults] = useState([]);
  const [gameResults, setGameResults] = useState([]);
  const [urlResults, setUrlResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const timeoutRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  const tmdbApiKey = process.env.REACT_APP_TMDB_API_KEY;
  const videoGamesApiKey = process.env.REACT_APP_RAWGIO_API_KEY;
  const page = 1;
  const ordering = '-playtime,-added';

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target) &&
        !inputRef.current.contains(e.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  // Auto-hide on scroll
  useEffect(() => {
    const handleScroll = () => {
      setShowDropdown(false);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchText(value);
    setShowDropdown(value.length > 0);

    if (timeoutRef.current) clearTimeout(timeoutRef.current);

    timeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 500);
  };

  const performSearch = async (query) => {
    if (!query) return;

    if (isValidUrl(query)) {
      setMovieResults([]);
      setTvResults([]);
      setGameResults([]);
      setUrlResults([
        { url: query, ratingCategory: 'Article' },
        { url: query, ratingCategory: 'Online Resource' },
        { url: query, ratingCategory: 'Misinformation/Disinformation' },
      ]);
      return;
    }

    try {
      const [gamesRes, moviesRes, tvRes] = await Promise.allSettled([
        axios.get(
          `https://api.rawg.io/api/games?key=${videoGamesApiKey}&ordering=${ordering}&exclude_stores=9&search=${query}&page_size=5`,
          { timeout: 2000 },
        ),
        axios.get(
          `https://api.themoviedb.org/3/search/movie?api_key=${tmdbApiKey}&query=${query}&include_adult=false&language=en-US&page=${page}`,
          { timeout: 2000 },
        ),
        axios.get(
          `https://api.themoviedb.org/3/search/tv?api_key=${tmdbApiKey}&query=${query}&include_adult=false&language=en-US&page=${page}`,
          { timeout: 2000 },
        ),
      ]);

      setGameResults(
        gamesRes.status === 'fulfilled' ? gamesRes.value.data.results : [],
      );
      setMovieResults(
        moviesRes.status === 'fulfilled' ? moviesRes.value.data.results : [],
      );
      setTvResults(
        tvRes.status === 'fulfilled' ? tvRes.value.data.results : [],
      );
      setUrlResults([]);
    } catch (err) {
      console.error('Error fetching data:', err);
    }
  };

  const renderUrlResults = () =>
    urlResults.map((item, idx) => (
      <NavLink
        key={idx}
        to={
          item.ratingCategory === 'Article'
            ? '/rate/articles'
            : item.ratingCategory === 'Online Resource'
              ? '/rate/online-resources'
              : '/rate/misinfo-disinfo'
        }
      >
        <div className="p-4 hover:bg-secondary/10 rounded-xl transition duration-200">
          <div className="font-semibold text-secondary truncate">
            {item.url}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            {item.ratingCategory}
          </div>
        </div>
      </NavLink>
    ));

  const renderSearchResults = (items, type) =>
    items.map((item) => (
      <NavLink key={item.id} to={`/rate/${type}/${item.id}`}>
        <div className="p-4 hover:bg-secondary/10 rounded-xl flex gap-3 items-center transition duration-200">
          <img
            src={
              type === 'game'
                ? item.background_image
                : `https://image.tmdb.org/t/p/original/${item.backdrop_path}`
            }
            alt={item.title || item.name}
            className="w-16 h-10 object-cover rounded-lg shadow-sm"
          />
          <div className="flex-1">
            <div className="font-semibold text-gray-800 text-base">
              {item.title || item.name}
            </div>
            <div className="text-sm text-gray-600 capitalize mt-1">
              {type} •{' '}
              {item.release_date?.substring(0, 4) ||
                item.first_air_date?.substring(0, 4)}
            </div>
          </div>
        </div>
      </NavLink>
    ));

  return (
    <div className="relative z-9999 w-full max-w-2xl mx-auto">
      <div className="relative">
        <input
          type="search"
          ref={inputRef}
          value={searchText}
          onChange={handleInputChange}
          onFocus={() => setShowDropdown(true)}
          placeholder="Search movies, TV shows, games, or URLs..."
          className="w-full h-16 px-6 pr-14 text-lg text-gray-800 bg-white/95 backdrop-blur-sm rounded-2xl border-0 
          focus:outline-none focus:ring-4 focus:ring-secondary/20 shadow-2xl placeholder:text-gray-500 
          transition-all duration-300 hover:shadow-3xl hover:bg-white"
        />

        {/* Search Icon */}
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400">
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute mt-4 w-full bg-white/95 backdrop-blur-lg shadow-2xl rounded-2xl max-h-96 overflow-y-auto border border-gray-100/50"
        >
          <div className="p-2 space-y-6">
            {urlResults.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3 text-secondary uppercase tracking-wide px-2">
                  URLs
                </h3>
                <div className="space-y-1">{renderUrlResults()}</div>
              </div>
            )}

            {movieResults.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3 text-secondary uppercase tracking-wide px-2">
                  Movies
                </h3>
                <div className="space-y-1">
                  {renderSearchResults(movieResults, 'movie')}
                </div>
              </div>
            )}

            {tvResults.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3 text-secondary uppercase tracking-wide px-2">
                  TV Shows
                </h3>
                <div className="space-y-1">
                  {renderSearchResults(tvResults, 'tv')}
                </div>
              </div>
            )}

            {gameResults.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold mb-3 text-secondary uppercase tracking-wide px-2">
                  Video Games
                </h3>
                <div className="space-y-1">
                  {renderSearchResults(gameResults, 'game')}
                </div>
              </div>
            )}
          </div>

          {movieResults.length === 0 &&
            tvResults.length === 0 &&
            gameResults.length === 0 &&
            urlResults.length === 0 && (
              <div className="text-gray-500 text-center py-8">
                No results found
              </div>
            )}
        </div>
      )}
    </div>
  );
};

export default HeaderSearchbar;
